import React, { useCallback, useState } from 'react';
import TabPanel from '../../ScenarioSettingsDialog/TabPanel.tsx';
import { Controller, useFormContext } from 'react-hook-form';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettings/hooks/useScenarioForm.ts';
import { Box, FormControlLabel, Switch, TextField } from '@mui/material';
import { createFormField } from 'app/utils/formUtils.ts';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';

import { PersonaDropdown } from '../ScenarioGeneralSettings/PersonaDropdown';
import { useScenarioPersonas } from '../ScenarioPersonasSettings/hooks/useScenarioPersonas';
import PersonaSelectionDialog from '../ScenarioPersonasSettings/PersonaSelectionDialog';
import PersonaDetailDialog from '../../../PersonasManager/PersonaDetailDialog/PersonaDetailDialog';
import { PersonaUsage } from '@/api-gen/api.schemas';
import type { PersonaDto } from '@/api-gen/api.schemas';
import { useRolesGetRoles } from '@/api-gen/api';

const ScenarioIntroAudioSettingsTab: React.FC<{ index: string; value: string }> = ({
  index,
  value
}) => {
  const { control, watch, setValue, trigger } = useFormContext<ScenarioSettingsType>();
  const field = createFormField<ScenarioSettingsType>();
  const [isPersonaDialogOpen, setIsPersonaDialogOpen] = useState(false);
  // State for persona edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPersonaForEdit, setSelectedPersonaForEdit] = useState<PersonaDto | null>(null);

  // Watch scenarioPersonas for changes
  const scenarioPersonas = watch(field.scenarioPersonas) || [];
  const isEnabled = !!watch(field.playScenarioContextOn);

  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);


  // Fetch roles for the scenario language to get the correct Instructor role
  const scenarioLanguage = watch(field.langCode);
  const { data: rolesResponse } = useRolesGetRoles(
    { langCode: scenarioLanguage || '' },
    { query: { enabled: !!scenarioLanguage, staleTime: 5 * 60 * 1000 } }
  );
  const roles = (rolesResponse as any)?.data || rolesResponse || [];

  // Find the correct Instructor role based on language
  const instructorRoles = roles.filter(
    (role: any) => role.name?.toLowerCase().includes('instructor') || role.name?.toLowerCase().includes('instruk') // Covers Instrukteur, Instruktör, Instruktør
  );
  const instructorRoleId: number | undefined = instructorRoles.length > 0 ? instructorRoles[0].id : undefined;

  // Use shared scenario personas hook
  const { introPersona, setIntroPersona, addPersona } = useScenarioPersonas(
    scenarioPersonas,
    setValue,
    trigger,
    translate
  );

  // Handle persona selection from PersonaDropdown
  const handleIntroPersonaChange = useCallback(
    (selectedPersonaId: number | null) => {
      setIntroPersona(selectedPersonaId);
    },
    [setIntroPersona]
  );

  // Handle add new persona action from PersonaDropdown
  const handleAddNewPersona = useCallback(() => {
    setIsPersonaDialogOpen(true);
  }, []);

  // Handle edit persona action from PersonaDropdown
  const handleEditPersona = useCallback((persona: PersonaDto) => {
    setSelectedPersonaForEdit(persona);
    setIsEditDialogOpen(true);
  }, []);

  // Handle adding new persona from dialog
  const handlePersonaAdd = useCallback(
    (persona: PersonaDto, usages: PersonaUsage[], roleId?: number | null) => {
      const result = addPersona(persona, usages, roleId);
      if (result.success) {
        setIsPersonaDialogOpen(false);
      } else {
        console.error('Failed to add persona:', result.message);
      }
    },
    [addPersona]
  );

  const existingPersonaIds = scenarioPersonas.map((sp) => sp.personaId).filter((id): id is number => id != null);

  return (
    <>
      <TabPanel index={index} value={value}>
        <Box>
          <Controller
            control={control}
            name={field.playScenarioContextOn}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                sx={{ mb: 1.5 }}
                control={
                  <Switch checked={!!value} onChange={(_, next) => onChange(next)} />
                }
                label={translate(
                  nameof.full<I18nScenarioEditorNs>((n) => n.scenarioIntroAudioSettingsTab.enable)
                )}
              />
            )}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              opacity: isEnabled ? 1 : 0.55,
              transition: 'opacity 0.15s'
            }}
          >
            <PersonaDropdown
              size="small"
              scenarioPersonas={scenarioPersonas}
              selectedPersona={introPersona}
              usageType={PersonaUsage.Intro}
              label={translate(
                nameof.full<I18nScenarioEditorNs>((n) => n.scenarioIntroAudioSettingsTab.introPersona)
              )}
              helperText={translate(
                nameof.full<I18nScenarioEditorNs>((n) => n.scenarioIntroAudioSettingsTab.introPersonaHelperText)
              )}
              placeholder={
                scenarioPersonas.length === 0
                  ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.noPersonasAvailable))
                  : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.none))
              }
              onPersonaChange={handleIntroPersonaChange}
              onAddNewPersona={handleAddNewPersona}
              onEditPersona={handleEditPersona}
              showEditButton={true}
              scenarioLanguage={watch(field.langCode)}
            />

            <Controller
              control={control}
              name={field.scenarioContextIntro}
              render={({ field: props }) => (
                <TextField
                  variant="outlined"
                  multiline
                  minRows={4}
                  maxRows={15}
                  fullWidth
                  label={translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioIntroAudioSettingsTab.introAudio)
                  )}
                  placeholder={translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioIntroAudioSettingsTab.writeAnIntroText)
                  )}
                  {...props}
                  value={props.value ?? ''}
                />
              )}
            />
          </Box>
        </Box>
      </TabPanel>

        <PersonaSelectionDialog
          open={isPersonaDialogOpen}
          onClose={() => setIsPersonaDialogOpen(false)}
          onPersonaSelect={(persona, _usages, _defaultRoleId) =>
            handlePersonaAdd(persona, [PersonaUsage.Intro], instructorRoleId)
          }
          existingPersonaIds={existingPersonaIds}
          scenarioLanguage={watch(field.langCode)}
        />

        <PersonaDetailDialog
          open={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setSelectedPersonaForEdit(null);
          }}
          personaId={selectedPersonaForEdit?.id}
          onRefresh={() => {
            // The personas in the scenario are embedded, so we don't need to refresh
            // The PersonaDetailDialog will handle its own data refresh
          }}
        />
    </>
  );
};

export default ScenarioIntroAudioSettingsTab;
